import * as React from 'react';
import { useMemo } from 'react';

import { Dialog, DialogActions, DialogButton, DialogContent, DialogTitle } from '@rmwc/dialog';
import '@rmwc/dialog/styles';

import { DataTable, DataTableContent, DataTableHead, DataTableRow, DataTableHeadCell, DataTableBody, DataTableCell } from '@rmwc/data-table';
import '@rmwc/data-table/styles';

import { Button } from '@rmwc/button';
import '@rmwc/button/styles';

import classNames = require('classnames');

import { useSiteUploads, useUpdateUpload } from './backend';
import { StateIndicator } from './state_indicator';
import { SiteData, UploadOverviewData } from '../types';
import { useClientSetting } from '../client_settings';
import { ClientSettingSwitch } from './client_setting_switch';
import { sortBy } from 'lodash';

const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' });

interface ToggleCellProps {
  list: string[];
}

const ToggleCell: React.FC<ToggleCellProps> = ({ list }) => {
  const [ open, setOpen] = React.useState(false);

  const text = list.join(', ');
  const openable = text.length >= 50;
  const display = open ? list.map(s => <><pre>{s}</pre></>) : text.slice(0, 50);
  const after = openable && !open ? '...' : '';
  const classes = classNames('summary_cell', { opened: open, closed: !open, actionable: openable });
  const sum = openable && !open ? `(${list.length})` : '';
  const handleClick = openable ? () => setOpen(!open) : undefined;

  return <DataTableCell className={classes} onClick={handleClick}>
    {display}{after}{sum}
  </DataTableCell>;
}

const useGetTrackerDisplayNames = (ids: string[], site: SiteData) => {
  const showClientNames = useClientSetting('showCustomerNames');

  const nameMap = useMemo(() => {
    const map = new Map();

    for(const tracker of site.trackers) {
      map.set(tracker.id, tracker.name);
    }

    return map;
  }, [site]);

  const names = useMemo(() => { 
    if(showClientNames) {
      return ids.map((id) => nameMap.get(id) ?? id);
    } else {
      return ids;
    }
  }, [ids, nameMap, showClientNames]);

  return useMemo(() => {
    return sortBy(names);
  }, [names]);
};

interface UploadsRowProps {
  doImport: (upload: string) => void;
  id: string;
  site: SiteData;
  upload: UploadOverviewData;
};

const UploadsRow: React.FC<UploadsRowProps> = ({ id, site, upload, doImport }) => {
  const state = upload.pending ? 'warning' : 'okay';
  const mutation = useUpdateUpload();

  const trackerDisplayNames = useGetTrackerDisplayNames(upload.trackerIds, site);

  const ignore = async () => {
    try {
      mutation.mutateAsync({
        site: id,
        upload: upload.id,
        data: { pending: false },
      });
    } catch(err: any) {
      alert(err.message);
    }
  };

  return (
    <DataTableRow>
      <DataTableCell className="state_cell"><StateIndicator state={state} /></DataTableCell>
      <DataTableCell className="note_cell">{upload.comment || <i>No comment</i>}</DataTableCell>
      <ToggleCell list={trackerDisplayNames} />
      <DataTableCell className="user_cell">{upload.userName ?? 'Unknown'}</DataTableCell>
      <DataTableCell className="date_cell">{formatter.format(upload.date)}</DataTableCell>
      <DataTableCell className="action_cell">
        <Button className="import" raised onClick={() => doImport(upload.id)}>Import</Button>
        <Button disabled={!upload.pending || mutation.isLoading} className="ignore" outlined onClick={ignore}>Ignore</Button>
      </DataTableCell>
    </DataTableRow>
  );
};

interface UploadsTableProps {
  id: string;
  site: SiteData;
  doImport: (upload: string) => void;
};

const UploadsTable: React.FC<UploadsTableProps> = ({ id, site, doImport }) => {
  const uploadsQuery = useSiteUploads(id);

  const uploads = useMemo(() => {
    if(uploadsQuery.data == null) {
      return undefined;
    }

    return sortBy(uploadsQuery.data, (e) => +e.date);
  }, [uploadsQuery.data]);

  let body;

  if(uploadsQuery.isError) {
    body = (
      <DataTableRow>
        <DataTableCell colSpan={5} className="message_row">
          Unable to get uploads
        </DataTableCell>
      </DataTableRow>
    );
  } else if(uploads == null) {
    body = (
      <DataTableRow>
        <DataTableCell colSpan={5} className="message_row">
          Loading ...
        </DataTableCell>
      </DataTableRow>
    );
  } else if(uploads.length === 0) {
    body = (
      <DataTableRow>
        <DataTableCell colSpan={5} className="message_row">
          No uploads on this site
        </DataTableCell>
      </DataTableRow>
    );
  } else {
    body = uploads.map((upload) => {
      return <UploadsRow key={upload.id} id={id} site={site} doImport={doImport} upload={upload} />;
    });
  }

  return (
    <DataTable className="uploads_table" stickyRows={1}>
      <DataTableContent>
        <DataTableHead>
          <DataTableRow>
            <DataTableHeadCell className="state_cell"></DataTableHeadCell>
            <DataTableHeadCell className="note_cell">Note</DataTableHeadCell>
            <DataTableHeadCell className="summary_cell">Tracker</DataTableHeadCell>
            <DataTableHeadCell className="user_cell">Uploader</DataTableHeadCell>
            <DataTableHeadCell className="date_cell">Date</DataTableHeadCell>
            <DataTableHeadCell className="action_cell"></DataTableHeadCell>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {body}
        </DataTableBody>
      </DataTableContent>
    </DataTable>
  );
};

export interface UploadsListDialogProps {
  id: string;
  site: SiteData;
  doImport: (upload: string) => void;
  onClosed: () => void;
};

export const UploadsListDialog: React.FC<UploadsListDialogProps> = (props) => {
  const { id, onClosed } = props;

  return (
    <Dialog className="upload_dialog" open={true} onClosed={onClosed}>
      <DialogTitle>
        <div className="upload_title">
          Uploads
          <ClientSettingSwitch setting="showCustomerNames" label="Show customer names" />
        </div>
      </DialogTitle>
      <DialogContent>
        <UploadsTable {...props} />
      </DialogContent>
      <DialogActions>
        <DialogButton isDefaultAction onClick={onClosed}>Close</DialogButton>
      </DialogActions>
    </Dialog>
  );
};
